import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';

const formatCompetencia = (comp: number) => {
  const s = String(comp);
  return `${s.substring(4, 6)}/${s.substring(0, 4)}`;
};

const EspecializadaProducao: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [unidadeFiltro, setUnidadeFiltro] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getProducao(municipioAtivo.id, dataInicio || undefined, dataFim || undefined, unidadeFiltro || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, dataInicio, dataFim, unidadeFiltro]);

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    return `Producao Especializada - ${municipioAtivo?.nome || 'Contagem/MG'}
Total atendimentos: ${Number(data.kpis?.total_atendimentos || 0).toLocaleString('pt-BR')}
Unidades ativas: ${data.kpis?.total_unidades || 0}
Especialidades: ${data.kpis?.total_especialidades || 0}
Media mensal: ${Number(data.kpis?.media_mensal || 0).toLocaleString('pt-BR')}`;
  }, [data, municipioAtivo]);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={500} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Gestão de Produção</Typography>
          <Typography variant="body2" color="text.secondary">Produção por unidade, profissional e procedimento</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Unidade</InputLabel>
            <Select value={unidadeFiltro} onChange={e => setUnidadeFiltro(e.target.value)} label="Unidade">
              <MenuItem value="">Todas</MenuItem>
              {(data?.producaoPorUnidade || []).map((u: any) => (
                <MenuItem key={u.codigo_unidade} value={u.codigo_unidade}>{(u.nome_unidade || '').substring(0, 40)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={(di, df) => { setDataInicio(di); setDataFim(df); }} />
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><KpiCard titulo="Total Atendimentos" valor={Number(data?.kpis?.total_atendimentos || 0).toLocaleString('pt-BR')} cor="#2563eb" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Unidades Ativas" valor={data?.kpis?.total_unidades || 0} cor="#16a34a" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Especialidades" valor={data?.kpis?.total_especialidades || 0} cor="#7c3aed" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Média Mensal" valor={Number(data?.kpis?.media_mensal || 0).toLocaleString('pt-BR')} cor="#ea580c" /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Evolução (granularidade dinâmica) */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {data?.granularidade === 'day' ? 'Evolução Diária' : data?.granularidade === 'year' ? 'Evolução Anual' : 'Evolução Mensal'}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(data?.evolucaoMensal || []).map((d: any) => {
                const dt = new Date(d.mes);
                let label: string;
                if (data?.granularidade === 'day') {
                  label = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                } else if (data?.granularidade === 'year') {
                  label = String(dt.getFullYear());
                } else {
                  label = dt.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
                }
                return { mes: label, atendimentos: Number(d.total_atendimentos) };
              })}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR'), 'Atendimentos']} />
                <Line type="monotone" dataKey="atendimentos" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* BPA por Competência */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Valor BPA por Competência</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={(data?.bpaPorCompetencia || []).map((b: any) => ({
                comp: formatCompetencia(b.anomescompetencia),
                valor: Number(b.total_valor),
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="comp" fontSize={10} angle={-45} textAnchor="end" height={50} />
                <YAxis fontSize={11} tickFormatter={(v: any) => `R$${(v/1000000).toFixed(1)}M`} />
                <Tooltip formatter={(v: any) => [`R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']} />
                <Bar dataKey="valor" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Profissionais */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Top 20 Especialidades por Produção</Typography>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={(data?.producaoPorProfissional || []).slice(0, 20).map((p: any) => ({
                nome: (p.profissional_especialidade || '').substring(0, 25),
                total: Number(p.total_atendimentos),
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <YAxis type="category" dataKey="nome" width={180} fontSize={9} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR'), 'Atendimentos']} />
                <Bar dataKey="total" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Produção por Unidade */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Produção por Unidade</Typography>
            <TableContainer sx={{ maxHeight: 450 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Atendimentos</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Especialidades</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Procedimentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.producaoPorUnidade || []).map((u: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{(u.nome_unidade || '').substring(0, 40)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{Number(u.total_atendimentos).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{u.total_especialidades}</TableCell>
                      <TableCell align="right">{u.total_procedimentos}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Procedimentos */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Top 20 Procedimentos</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total Atendimentos</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unidades Executantes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.topProcedimentos || []).map((p: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{p.nome_procedimento}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{Number(p.total_atendimentos).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{p.unidades_executantes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <ChatIAButton contexto="dashboard" dadosContexto={dadosContexto} />
    </Box>
  );
};

export default EspecializadaProducao;
